import React from "react";
import { rankSources } from "../lib/sourceSignature/source-score";

type Averages = {
  nh3_mg_l?: number; bod_mg_l?: number; cod_mg_l?: number; chloride_mg_l?: number;
  color_hazen?: number; do_mg_l?: number; toc_mg_l?: number; doc_mg_l?: number;
  ph?: number; orp_mv?: number; temp_c?: number; tss_mg_l?: number;
  turbidity_ntu?: number; ec_us_cm?: number;
};

export default function SourceSignaturePanel({
  averages,
  maxExpanded = 2,
}: {
  averages: Averages;
  maxExpanded?: number;
}) {
  const ranked = rankSources(averages);

  const card: React.CSSProperties = {
    width: "100%",
    borderRadius: 12,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  };

  const header: React.CSSProperties = {
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
  };

  const body: React.CSSProperties = {
    padding: 12,
    maxHeight: 360,              // keeps it short
    overflowY: "auto",           // adds a small scrollbar only if needed
  };

if (!ranked.length) {
  return (
    <div style={{
      width: "100%",
      borderRadius: 12,
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        padding: "10px 12px",
        borderBottom: "1px solid #e5e7eb",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
      }}>
        <div style={{ fontWeight: 700, color: "#111827" }}>Likely Source (now)</div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>Based on station averages</div>
      </div>
      <div style={{ padding: 12, maxHeight: 360, overflowY: "auto" }}>
        <div style={{
          fontSize: 12, color: "#4b5563",
          border: "1px dashed #e5e7eb",
          borderRadius: 8,
          padding: "8px 10px",
          background: "#ffffff"
        }}>
          No station data in the selected time range. Try a broader range.
        </div>
      </div>
    </div>
  );
}


  const expanded = ranked.slice(0, maxExpanded);
  const condensed = ranked.slice(maxExpanded);

  const barBg = { height: 6, borderRadius: 9999, background: "#eef2f7" };
  const barFill = (w: number): React.CSSProperties => ({
    width: `${Math.min(100, w * 100)}%`,
    height: 6,
    borderRadius: 9999,
    background: "#0ea5e9" // calm blue
  });

  return (
    <div style={card}>
      <div style={header}>
        <div style={{ fontWeight: 700, color: "#111827" }}>Likely Source (now)</div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>Based on station averages</div>
      </div>

      <div style={body}>
        {/* Expanded rows */}
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
          {expanded.map((r, i) => (
            <li key={r.key} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "8px 10px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: 10, display: "grid", placeItems: "center",
                    background: "#f3f4f6", color: "#111827", fontSize: 12, fontWeight: 700
                  }}>{i + 1}</span>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{r.label}</div>
                </div>
                <div style={{ fontSize: 11, color: "#374151", fontWeight: 600 }}>{r.score.toFixed(2)}</div>
              </div>
              <div style={{ marginTop: 6, ...barBg }}>
                <div style={barFill(r.score)} />
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: "#4b5563" }}>{r.explain}</div>
              {i === 0 && (
                <div style={{
                  marginTop: 6, fontSize: 10, color: "#374151",
                  background: "#f9fafb", border: "1px solid #e5e7eb",
                  padding: "2px 6px", borderRadius: 9999, display: "inline-block"
                }}>
                  Top signal
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* Condensed chips */}
        {condensed.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", marginBottom: 6 }}>Other signals</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {condensed.map((r) => (
                <span key={r.key} title={`${r.explain} (${r.score.toFixed(2)})`} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 10px", borderRadius: 9999,
                  border: "1px solid #e5e7eb", background: "#ffffff",
                  fontSize: 12, color: "#374151", fontWeight: 600
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: 9999, background: "#0ea5e9" }} />
                  {r.label}
                  <span style={{ fontSize: 11, opacity: 0.8 }}>· {r.score.toFixed(2)}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
